import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useIsMobile } from '@/hooks/useIsMobile';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import { ENVIRONMENT_STYLE } from '@/environment/store';
import type { ApiError } from '@/api/client';
import type { UserSummary } from '@/types/api';

interface KickoutDialogProps {
  user: UserSummary | null;
  pending: boolean;
  error: ApiError | null;
  onCancel: () => void;
  onConfirm: (confirmUsername: string, reason: string) => void;
}

/**
 * Confirmation for deleting a SEP account permanently.
 *
 * Deliberately more work than a normal confirm dialog. Everything else in this
 * app can be undone by clicking the other way; this cannot be undone at all,
 * and SEP deletes the master data with the account, so there is nothing left to
 * restore from. The cost of the extra typing is paid once per deletion; the
 * cost of a mis-click is permanent.
 *
 * Three things do the work:
 *
 * * **The username must be typed, not clicked.** It defeats the muscle memory
 *   of confirming without reading, and it is the same value the server checks —
 *   so a dialog aimed at the wrong row cannot produce a matching string.
 * * **A reason is required.** SEP keeps nothing afterwards, so the audit row is
 *   the only surviving record of why an account stopped existing.
 * * **The environment is named.** The switcher is up in the app bar, a long way
 *   from this button, and "which system am I deleting from" is exactly the
 *   question a destructive dialog should not make you scroll to answer.
 *
 * Mount this with `key={user?.username}` so React discards the typed
 * confirmation when the target changes. Carrying a half-typed name across from
 * a dialog that was closed is exactly the state in which the wrong account gets
 * deleted, and a remount cannot forget to clear it the way a reset can.
 */
export function KickoutDialog({
  user,
  pending,
  error,
  onCancel,
  onConfirm,
}: KickoutDialogProps) {
  const isMobile = useIsMobile();
  const environment = useEnvironment();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');

  if (!user) return null;

  const matches = typed.trim().toLowerCase() === user.username.toLowerCase();
  const ready = matches && reason.trim().length > 0 && !pending;
  const style = ENVIRONMENT_STYLE[environment];

  return (
    <Dialog
      open
      onClose={pending ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>Benutzer endgültig löschen</DialogTitle>
      {pending && <LinearProgress />}
      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="error">
            <strong>{user.username}</strong> wird in SEP vollständig gelöscht —
            inklusive Stammdaten. Das lässt sich <strong>nicht</strong> rückgängig
            machen.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Umgebung <strong>{style.label}</strong> · Mandant{' '}
            <strong>{user.tenant}</strong> · Rolle{' '}
            <strong>{user.sepRole ?? '—'}</strong>
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Soll der Zugang nur vorübergehend entzogen werden, ist das hier der
            falsche Weg — dafür genügt eine Benutzer-Ausnahme «gesperrt» in der
            Lizenzverwaltung, reversibel und auf Wunsch befristet. Ein
            Mandantenwechsel sperrt nichts; er ändert nur, welche Lizenzen
            gelten.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Hinweis: Das SEP-Login selbst kann nur der Nutzer über «Account
            löschen» auf seiner SEP-Profilseite deaktivieren — reversibel, die
            Stammdaten bleiben erhalten. Für fremde Konten ist das als Admin
            nicht möglich.
          </Typography>

          <TextField
            label="Zur Bestätigung den Benutzernamen eingeben"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            disabled={pending}
            fullWidth
            autoFocus
            autoComplete="off"
            placeholder={user.username}
            error={typed.length > 0 && !matches}
            helperText={
              typed.length > 0 && !matches ? 'Stimmt nicht überein.' : ' '
            }
          />

          <TextField
            label="Grund (wird protokolliert)"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            disabled={pending}
            fullWidth
            multiline
            minRows={2}
            helperText="Nach der Löschung ist dies der einzige verbleibende Nachweis."
          />

          {error && <Alert severity="error">{error.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={pending} sx={{ textTransform: 'none' }}>
          Abbrechen
        </Button>
        <Box>
          <Button
            variant="contained"
            color="error"
            disabled={!ready}
            onClick={() => onConfirm(typed.trim(), reason.trim())}
            sx={{ textTransform: 'none' }}
          >
            {pending ? 'Wird gelöscht…' : 'Endgültig löschen'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
